import { useEffect, useState } from 'react'

import { useAppDispatch } from '@/utils/typeHelpers'
import { fetchCatchUpBKCoupon } from '@/components/CatchUpBookkeepingStatus/catchUpBookkeepingStatus.slice'

type Coupon = Awaited<ReturnType<ReturnType<typeof fetchCatchUpBKCoupon>>>

type Settled = { argsKey: string; coupon: Coupon | undefined }

/**
 * Returns the coupon for the current args only. With `keepPreviousWhileLoading`
 * the previous args' coupon stays visible until the new reply lands.
 */
export const useLatestCatchUpBkCoupon = (
  isAnnual: boolean,
  productId?: string,
  keepPreviousWhileLoading = false
): { coupon: Coupon | undefined; isLoading: boolean } => {
  const dispatch = useAppDispatch()
  const [settled, setSettled] = useState<Settled>()
  const argsKey = `${isAnnual}:${productId ?? ''}`

  useEffect(() => {
    let isStale = false
    const request = productId
      ? fetchCatchUpBKCoupon(isAnnual, productId)
      : fetchCatchUpBKCoupon(isAnnual)
    dispatch(request)
      .catch(() => undefined)
      .then((coupon) => {
        // A slower reply for earlier args must not overwrite the current one
        if (!isStale) {
          setSettled({ argsKey, coupon })
        }
      })
    return () => {
      isStale = true
    }
  }, [dispatch, isAnnual, productId, argsKey])

  const isCurrent = settled?.argsKey === argsKey
  return {
    coupon: isCurrent || keepPreviousWhileLoading ? settled?.coupon : undefined,
    isLoading: !isCurrent,
  }
}
